/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';
import {Disposable} from '@siemens/ix';
import {BatteryAnalyticsDto} from '@product-passport/passport/dto';
import {PassportService} from '../../../../services/passport.service';
import {ECElementEvent, EChartsOption} from 'echarts';
import {TranslateService} from '@ngx-translate/core';
import {ModalCreateBatteryPassComponent} from '../modal-create-battery-pass/modal-create-battery-pass.component';
import {ModalService, themeSwitcher} from '@siemens/ix-angular';
import {Router} from '@angular/router';
import {SearchCriteriaStatus} from '../../../../models/SearchCriteria';
import {ThemeService} from '../../../../services/theme.service';

const CONFIGURED_DECIMAL_PLACES = 2;
const CONFIGURED_100_PERCENT = 100;
const CHART_BUILD_TIME_WAIT = 200;
const MAX_WEEKS_IN_ONE_YEAR = 53;
const DAYS_IN_WEEK = 7;

@Component({
  selector: 'app-battery-dashboard',
  templateUrl: './battery-dashboard.component.html',
  styleUrl: './battery-dashboard.component.scss',
  standalone: false,
})
export class BatteryDashboardComponent implements OnInit, OnDestroy {
  private readonly passportService = inject(PassportService);
  private readonly translate = inject(TranslateService);
  private readonly modalService = inject(ModalService);
  private readonly router = inject(Router);
  private readonly themeService = inject(ThemeService);

  analyticsLoading = true;
  analyticsEmpty = true;

  private langChangeSubscription: Subscription | null = null;
  private schemaChangedDisposable: Disposable | null = null;
  analytics: BatteryAnalyticsDto = {
    statistics: {
      totalPassports: 0,
      passportsInDraftState: 0,
      passportsInCompletedState: 0,
    },
  };

  pieChart: EChartsOption | null = null;
  barChart: EChartsOption | null = null;
  lineChart: EChartsOption | null = null;

  async ngOnInit() {
    this.analyticsLoading = true;

    this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
      setTimeout(() => {
        this.rebuildCharts();
      }, CHART_BUILD_TIME_WAIT);
    });

    this.schemaChangedDisposable = themeSwitcher.schemaChanged.on(() => {
      this.rebuildCharts();
    });

    this.analytics = await this.passportService.loadAnalytics();
    this.analyticsEmpty = this.analytics.statistics?.totalPassports === 0;
    this.analyticsLoading = false;

    setTimeout(() => {
      this.rebuildCharts();
    }, CHART_BUILD_TIME_WAIT);
  }

  ngOnDestroy(): void {
    this.langChangeSubscription?.unsubscribe();
    this.schemaChangedDisposable?.dispose();
  }

  private rebuildCharts(): void {
    this.pieChart = this.buildPieChart();
    this.barChart = this.buildBarChart();
    this.lineChart = this.buildLineChart();
  }

  async addNewPassportClick() {
    await this.modalService.open({
      size: '840',
      content: ModalCreateBatteryPassComponent,
      centered: true,
      closeOnBackdropClick: true,
    });
  }

  buildPieChart(): EChartsOption {
    return {
      legend: {
        bottom: 0,
        selectedMode: false,
      },
      tooltip: {},
      series: [
        {
          name: this.translate.instant('overview.tabs.overview_tab.card_chart'),
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          label: {
            show: false,
            position: 'center',
          },
          color: [
            window
              .getComputedStyle(document.body)
              .getPropertyValue('--theme-color-success'),
            window
              .getComputedStyle(document.body)
              .getPropertyValue('--theme-color-critical'),
          ],
          data: [
            {
              value: this.analytics.statistics?.passportsInCompletedState,
              name: this.translate.instant('battery_pass.list.complete'),
              status: 'complete',
            },
            {
              value: this.analytics.statistics?.passportsInDraftState,
              name: this.translate.instant('battery_pass.list.draft'),
              status: 'draft',
            },
          ] as Array<{value: number | undefined; name: string; status: string}>,
        },
      ],
    };
  }

  async onPieChartClick(event: ECElementEvent) {
    const data = event.data as {status: string};
    if (event?.seriesType === 'pie') {
      await this.router.navigate(['app/list-passports'], {
        queryParams: {
          passportStatus: data.status as SearchCriteriaStatus,
        },
      });
    }
  }

  buildBarChart(): EChartsOption {
    return {
      tooltip: {
        trigger: 'axis',
        valueFormatter: (value) =>
          Number(value).toFixed(CONFIGURED_DECIMAL_PLACES) + ' %',
      },
      legend: {
        bottom: 0,
        selectedMode: false,
      },
      grid: {
        show: false,
        top: 0,
        bottom: '30px',
      },
      xAxis: {
        show: false,
        max: 100,
      },
      yAxis: {
        data: [''],
        show: false,
      },
      series: [
        {
          name: this.translate.instant(
            'overview.tabs.overview_tab.renewable_category_1',
          ),
          type: 'bar',
          stack: 'total',
          emphasis: {
            focus: 'series',
          },
          data: [this.analytics.statistics?.averageRecycledNickelShare ?? 0],
        },
        {
          name: this.translate.instant(
            'overview.tabs.overview_tab.renewable_category_2',
          ),
          type: 'bar',
          stack: 'total',
          emphasis: {
            focus: 'series',
          },
          data: [
            Number(
              CONFIGURED_100_PERCENT -
                (this.analytics.statistics?.averageRecycledNickelShare ?? 0),
            ).toFixed(CONFIGURED_DECIMAL_PLACES),
          ],
        },
      ],
    };
  }

  buildLineChart(): EChartsOption {
    const weeks =
      this.analytics.statistics?.averageCarbonFootprintPerWeek?.weeks ?? [];
    const values =
      this.analytics.statistics?.averageCarbonFootprintPerWeek?.values ?? [];
    const data = weeks.map((week, i) => [
      this.getFirstMondayOfWeek(week),
      values[i],
    ]);

    return {
      xAxis: {
        type: 'time',
      },
      tooltip: {
        trigger: 'axis',
      },
      grid: {
        top: '40px',
        left: '20px',
        right: '50px',
        bottom: '40px',
        containLabel: true,
      },
      yAxis: {
        type: 'value',
        name: this.translate.instant('general.units.kgCO2e_kWh'),
      },
      series: [
        {
          data: data,
          type: 'line',
          areaStyle: {},
          name: this.translate.instant(
            'overview.tabs.overview_tab.card_emission_total',
          ),
        },
      ],
    };
  }

  /**
   * Converts an ISO week label of the form `"YYYY-WW"` to the `Date` of the
   * Monday that starts that week.
   *
   * The algorithm follows the ISO 8601 definition: week 1 is the week that
   * contains 4 January (i.e. the first Thursday of the year).  The Monday of
   * week 1 is therefore calculated from 4 January, and subsequent weeks are
   * offset by multiples of seven days.
   *
   * An edge-case guard ensures that labels such as `"2021-53"` — which do not
   * exist for years with only 52 ISO weeks — are rejected by verifying that
   * the computed Monday still belongs to the stated calendar year.
   *
   * @param weekYear - ISO week label in the format `"YYYY-WW"` (e.g. `"2026-14"`).
   * @returns The `Date` of the Monday opening that ISO week, or `undefined` when
   *   the input is missing, malformed, the year is not a positive integer, the
   *   week number is outside the valid range 1–53, or the week does not exist
   *   in the given year (e.g. week 53 in a 52-week year).
   */
  getFirstMondayOfWeek(weekYear: string): Date | undefined {
    if (!weekYear || !weekYear.includes('-')) {
      return undefined;
    }

    const parts = weekYear.split('-');
    const year = Number(parts[0]);
    const week = Number(parts[1]);

    // Validate year is a finite positive integer
    if (!Number.isInteger(year) || year <= 0) {
      return undefined;
    }

    // Validate week is an integer in range 1–53
    if (!Number.isInteger(week) || week < 1 || week > MAX_WEEKS_IN_ONE_YEAR) {
      return undefined;
    }

    // Get to Monday of first ISO week of year (4 Jan is always in week 1)
    const firstMonday = new Date(year, 0, 4);
    firstMonday.setDate(firstMonday.getDate() + (1 - firstMonday.getDay()));

    // Add required weeks
    firstMonday.setDate(firstMonday.getDate() + DAYS_IN_WEEK * (week - 1));

    // Reject if the computed date has spilled into the next calendar year
    // (e.g. week 53 in a year that only has 52 ISO weeks)
    if (firstMonday.getFullYear() > year) {
      return undefined;
    }

    return firstMonday;
  }

  getChartTheme() {
    return this.themeService.actualChartTheme;
  }
}
